import { supabase } from "./supabase";
import { semColunasGeradas } from "./colunasGeradas";

// Fila local do que NÃO conseguiu gravar.
//
// A tela mostra o resultado antes de o banco confirmar — é o que deixa o CRM
// rápido. O preço é este: quando a gravação falha, o que está na tela existe
// só ali. Até aqui, quem fechasse a aba antes de ler o aviso perdia o
// trabalho, e foi assim que uma ficha inteira sumiu sem ninguém entender.
//
// Agora o que falhou fica guardado NO NAVEGADOR e é reenviado sozinho na
// próxima abertura. Não é sincronização offline completa: é uma rede de
// segurança para a falha pontual (queda de rede, servidor fora, sessão
// expirada), que é o caso real.
//
// Guardar dado de cliente no localStorage é uma escolha consciente: ou fica
// aqui até subir, ou some. O item sai da fila assim que grava.

const CHAVE = "crm_fila_gravacao_v1";
const LIMITE = 100;                       // teto de segurança contra crescer sem fim
const VALIDADE_MS = 30 * 24 * 3600 * 1000; // 30 dias: passou disso, o dado envelheceu

const ler = () => {
  try {
    const bruto = localStorage.getItem(CHAVE);
    const itens = bruto ? JSON.parse(bruto) : [];
    return Array.isArray(itens) ? itens : [];
  } catch { return []; }
};

const escrever = (itens) => {
  try { localStorage.setItem(CHAVE, JSON.stringify(itens.slice(-LIMITE))); }
  catch { /* quota cheia ou navegação privada: seguimos sem a rede de segurança */ }
};

const avisar = () => window.dispatchEvent(new CustomEvent("crm-fila-mudou", { detail: { total: pendentes().length } }));

/** O que está esperando para subir (já sem o que envelheceu). */
export function pendentes() {
  const limite = Date.now() - VALIDADE_MS;
  return ler().filter((i) => (i.ts || 0) > limite);
}

/**
 * Guarda uma linha que não gravou.
 *
 * `emColunas` diz se o objeto já está no formato do banco (snake_case, como o
 * formulário público monta) ou no formato do app (camelCase, convertido na
 * hora de enviar). Sem essa distinção, uma das duas origens subiria torta.
 */
export function enfileirar(tabela, dados, { emColunas = false, userId = null } = {}) {
  if (!tabela || !dados?.id) return;
  const fila = pendentes().filter((i) => !(i.tabela === tabela && i.dados?.id === dados.id));
  fila.push({ tabela, dados, emColunas, userId, ts: Date.now(), tentativas: 0 });
  escrever(fila);
  avisar();
}

export function enfileirarVarios(tabela, linhas, opcoes) {
  for (const linha of linhas || []) enfileirar(tabela, linha, opcoes);
}

/**
 * Tenta subir tudo o que está esperando. Item que grava sai da fila; item que
 * falha de novo continua lá para a próxima abertura.
 *
 * Devolve { enviados, restantes } — quem chama decide se mostra algo.
 */
export async function enviarPendentes(rowToDb) {
  const fila = pendentes();
  if (!fila.length) return { enviados: 0, restantes: 0 };

  const sobraram = [];
  let enviados = 0;

  for (const item of fila) {
    try {
      // Item antigo da fila pode ter guardado uma coluna gerada (ver
      // colunasGeradas.js); tirá-la aqui é o que deixa ele subir agora.
      const linha = item.emColunas
        ? semColunasGeradas(item.tabela, item.dados)
        : rowToDb(item.dados, item.userId, item.tabela);
      const { error } = await supabase.from(item.tabela).upsert(linha, { onConflict: "id" });
      if (error) throw new Error(error.message);
      enviados++;
    } catch (e) {
      // Erro de dado (coluna inexistente, violação de regra) não melhora com
      // tentativa: fica na fila com o motivo à vista, para alguém decidir.
      sobraram.push({ ...item, tentativas: (item.tentativas || 0) + 1, ultimoErro: e?.message || String(e) });
    }
  }

  escrever(sobraram);
  avisar();
  return { enviados, restantes: sobraram.length };
}

/** Descarta a fila (uso do TI, quando o dado já foi resolvido na mão). */
export function limparFila() {
  escrever([]);
  avisar();
}
